import {useEffect, useRef, useMemo} from 'react';
import {AGGREGATE_FIELDS} from 'js/helpers/search/AggregateSearchHelper';

/**
 * Prunes stale saved-search filter values that are not available in the
 * current aggregate options. Operates per-field and is reversible:
 *  - Only prunes the specific field(s) that changed.
 *  - If a field reverts to its original snapshot state, restores pruned values.
 *  - Batches all field updates into a single filters.update() call.
 */

const toLower = v => (typeof v === 'string' ? v.toLowerCase() : v);

const arraysEqualUnordered = (a, b) => {
    if (a.length !== b.length) return false;
    const setB = new Set(b.map(toLower));
    return a.every(v => setB.has(toLower(v)));
};

const usePruneSavedSearchFilters = ({
    filters,
    aggregateFilterOptions,
    savedSearchFilters,
    isSavedSearch,
    statusType,
}) => {
    const savedSearchSnapshotRef = useRef(null);
    const prevAggregateValuesRef = useRef(null);
    const prunedFieldsMapRef = useRef({});

    // Stable identity for savedSearchFilters – only changes when content changes
    const savedSearchKey = useMemo(
        () => (savedSearchFilters ? JSON.stringify(savedSearchFilters) : null),
        [savedSearchFilters],
    );

    // Capture saved search values and initial aggregate field values on mount
    useEffect(() => {
        if (isSavedSearch && savedSearchFilters) {
            const snapshot = {};
            AGGREGATE_FIELDS.forEach(field => {
                const values = savedSearchFilters[statusType]?.[field];
                if (Array.isArray(values) && values.length > 0) {
                    snapshot[field] = [...values];
                }
            });
            savedSearchSnapshotRef.current = Object.keys(snapshot).length > 0 ? snapshot : null;

            // Capture initial values to compare against for detecting user interaction
            const initialValues = {};
            AGGREGATE_FIELDS.forEach(field => {
                initialValues[field] = filters.value[statusType]?.[field] || [];
            });
            prevAggregateValuesRef.current = initialValues;
            prunedFieldsMapRef.current = {};
        } else {
            savedSearchSnapshotRef.current = null;
            prevAggregateValuesRef.current = null;
            prunedFieldsMapRef.current = {};
        }
    }, [isSavedSearch, savedSearchKey]);

    // Detect per-field changes, batch prune/restore in a single update
    useEffect(() => {
        if (!savedSearchSnapshotRef.current) return;
        if (!prevAggregateValuesRef.current) return;
        if (!aggregateFilterOptions) return;

        const snapshot = savedSearchSnapshotRef.current;
        const fieldsToUpdate = {};

        // Detect which fields changed
        const changedFields = AGGREGATE_FIELDS.filter(field => {
            const prev = prevAggregateValuesRef.current[field] || [];
            const curr = filters.value[statusType]?.[field] || [];
            return !arraysEqualUnordered(prev, curr);
        });

        if (changedFields.length === 0) return;

        // Process each changed field
        changedFields.forEach(field => {
            const savedValues = snapshot[field];
            if (!savedValues) return;

            const currentValues = filters.value[statusType]?.[field] || [];
            const validOptions = aggregateFilterOptions[field];

            // Skip if aggregate options haven't loaded yet (undefined/null).
            // An empty array means the API returned no valid options — prune all values.
            if (!Array.isArray(validOptions)) return;

            const validSet = new Set(validOptions.map(toLower));
            const previouslyPruned = prunedFieldsMapRef.current[field] || [];

            // Reconstruct logical selection: current + previously pruned (no duplicates)
            const currentLowerSet = new Set(currentValues.map(toLower));
            const reconstructed = [
                ...currentValues,
                ...previouslyPruned.filter(v => !currentLowerSet.has(toLower(v))),
            ];

            // Check if reconstructed set matches snapshot (user reverted to original)
            if (arraysEqualUnordered(reconstructed, savedValues)) {
                // Restore: return snapshot values (user reverted to original state)
                fieldsToUpdate[field] = [...savedValues];
                prunedFieldsMapRef.current[field] = [];
            } else {
                // Prune: keep only values present in valid options
                const keptValues = currentValues.filter(v => validSet.has(toLower(v)));
                const prunedValues = currentValues.filter(v => !validSet.has(toLower(v)));

                if (prunedValues.length > 0) {
                    fieldsToUpdate[field] = keptValues;
                    // Accumulate pruned values (no duplicates)
                    const existingPrunedSet = new Set(previouslyPruned.map(toLower));
                    prunedFieldsMapRef.current[field] = [
                        ...previouslyPruned,
                        ...prunedValues.filter(v => !existingPrunedSet.has(toLower(v))),
                    ];
                }
            }
        });

        // Update prev values for all changed fields
        changedFields.forEach(field => {
            prevAggregateValuesRef.current[field] = fieldsToUpdate[field] ?? filters.value[statusType]?.[field] ?? [];
        });

        // Single batched update if any fields need changing
        if (Object.keys(fieldsToUpdate).length > 0) {
            filters.update(fieldsToUpdate, true);
        }
    }, [filters.value, statusType, aggregateFilterOptions]);
};

export default usePruneSavedSearchFilters;
